import React, { useReducer } from "react";
import { useDispatch } from "react-redux";
import { setMessageHandler } from "../../../reducers/selectedValues";
import { Box } from "@mui/material";
import { InputText } from "../../uiControls/inputText";
import { ComboBox } from "../../uiControls/comboBox";
import useHttpRequests from "../../../hooks/useHttpRequests";
import { MessageDisplay } from "../../common/messageDisplay";
import { SaveCancel } from "../../uiControls/saveCancel";
import { StatusControl } from "../../uiControls/statusControl";
import * as utils from "../../common/utils";

const initialTimezoneIsValid = {
  description: "",
  countryName: "",
  diff: "",
};

export const TimezoneUpdate = (props) => {
  const {
    setUpdateOpen,
    timezoneEdit,
    setTimezoneEdit,
    timezoneAttributes,
    timezoneOffsets,
  } = props;
  const messageDispatch = useDispatch();
  const { sendRequest: updateTimezone } = useHttpRequests();

  const [timezoneDetails, setTimezoneDetails] = useReducer(
    (state, updates) => ({ ...state, ...updates }),
    timezoneAttributes
  );

  const [isValid, setIsValid] = useReducer(
    (state, updates) => ({ ...state, ...updates }),
    initialTimezoneIsValid
  );

  const updateTimezoneDetailsValue = (_key, value) => {
    setTimezoneDetails({ [_key]: value });
    if (value) {
      updateErrorMessage([_key], "", "");
    }
  };

  const updateErrorMessage = (_key, header, errMsg) => {
    setIsValid({ [_key]: errMsg + header });
  };

  const onChangeHandler = (e, controlName) => {
    const value = e.target.value;

    updateTimezoneDetailsValue(
      controlName,
      e.target.type === "checkbox" ? e.target.checked : value
    );
  };

  const timezoneOffsetChangeHandler = (e, newValue) => {
    if (newValue?.value) {
      updateTimezoneDetailsValue("diff", newValue.value);
    } else {
      updateTimezoneDetailsValue("diff", 0);
    }
  };

  const cancelTimezoneHandler = () => {
    messageDispatch(setMessageHandler({ type: null, message: null }));
    setTimezoneEdit(false);
    setUpdateOpen(false);
  };

  const validateTimezoneDetails = () => {
    setIsValid(initialTimezoneIsValid);

    var errorSummary = "";

    if (!timezoneDetails.countryName) {
      updateErrorMessage("countryName", "Country Name", "Enter ");
      errorSummary += "Enter country name \n";
    } else if (!utils.isValidCharacters(timezoneDetails.countryName)) {
      updateErrorMessage(
        "countryName",
        "country name",
        " has restricted characters"
      );
      errorSummary += "Country name has restricted characters \n";
    }

    if (!timezoneDetails.description) {
      updateErrorMessage("description", "Timezone Description", "Enter ");
      errorSummary += "Enter timezone description \n";
    } else if (!utils.isValidCharacters(timezoneDetails.description)) {
      updateErrorMessage(
        "description",
        "Timezone description",
        " has restricted characters"
      );
      errorSummary += "Timezone description has restricted characters \n";
    }

    const diffValue = parseFloat(timezoneDetails.diff);
    if (isNaN(diffValue) || diffValue < -12 || diffValue > 14) {
      updateErrorMessage("diff", "Difference", "Enter ");
      errorSummary += "Invalid GMT difference \n";
    }

    if (errorSummary !== "") {
      messageDispatch(
        setMessageHandler({ type: "error", message: errorSummary })
      );
    } else {
      const dataObj = timezoneDetails;

      const getUpdateTimezoneResponse = (resObj) => {
        if (
          !resObj ||
          !resObj["isSuccess"] ||
          (resObj["validationErrors"] !== undefined &&
            resObj["validationErrors"]?.length > 0)
        ) {
          messageDispatch(
            setMessageHandler({
              type: "error",
              message: resObj["message"]
                ? resObj["message"]
                : "" +
                  (resObj["validationErrors"]?.length > 0
                    ? "\n" + resObj["validationErrors"]?.join("\n")
                    : ""),
            })
          );
          return;
        }
        messageDispatch(
          setMessageHandler({
            type: "success",
            message: "Timezone updated successfully!",
          })
        );
        setTimeout(() => {
          cancelTimezoneHandler();
        }, 2000);
      };

      if (timezoneEdit) {
        updateTimezone(
          {
            url: "Timezones/" + dataObj["timezoneKey"],
            method: "PUT",
            body: dataObj,
          },
          getUpdateTimezoneResponse
        );
      } else {
        delete dataObj["timezoneKey"];
        updateTimezone(
          { url: "Timezones", body: dataObj },
          getUpdateTimezoneResponse
        );
      }
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          overflow: "hidden",
          padding: "0px",
        }}
      >
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          <MessageDisplay />
          <InputText
            type="text"
            label="Country Name"
            id={"timezoneDetails.countryName"}
            value={timezoneDetails.countryName}
            onChange={(e) => onChangeHandler(e, "countryName")}
            isRequired={true}
            isError={isValid["countryName"] !== ""}
            charLength={50}
          />

          <InputText
            type="text"
            label="Timezone Description"
            id={"timezoneDetails.description"}
            value={timezoneDetails.description}
            onChange={(e) => onChangeHandler(e, "description")}
            isRequired={true}
            isError={isValid["description"] !== ""}
            charLength={50}
          />

          <ComboBox
            id={"timezoneDetails.diff"}
            label="GMT Difference"
            isError={isValid["diff"] !== ""}
            isRequired={true}
            isDisabled={false}
            selectedValue={timezoneDetails.timezoneOffsetTitle}
            list={timezoneOffsets}
            onChange={timezoneOffsetChangeHandler}
          />

          <StatusControl
            label="DST"
            isRequired={false}
            fieldName={"isDst"}
            active={timezoneDetails.isDst}
            updateStatusValue={updateTimezoneDetailsValue}
          />

          <StatusControl
            label="Active"
            isRequired={false}
            fieldName={"active"}
            active={timezoneDetails.active}
            updateStatusValue={updateTimezoneDetailsValue}
          />
          <SaveCancel
            cancelCall={() => cancelTimezoneHandler()}
            saveCall={() => validateTimezoneDetails()}
          />
        </div>
      </Box>
    </>
  );
};

/*
import React, { useReducer } from "react";
import { useDispatch } from "react-redux";
import { setMessageHandler } from "../../../reducers/selectedValues";
import { Box } from "@mui/material";
import { InputText } from "../../uiControls/inputText";
import useHttpRequests from "../../../hooks/useHttpRequests";
import { MessageDisplay } from "../../common/messageDisplay";
import { SaveCancel } from "../../uiControls/saveCancel";
import { StatusControl } from "../../uiControls/statusControl";
import * as utils from "../../common/utils";

const initialTimezoneIsValid = {
  description: "",
  countryName: "",
  diff: "",
};

export const TimezoneUpdate = (props) => {
  const { setUpdateOpen, timezoneEdit, setTimezoneEdit, timezoneAttributes } =
    props;
  const messageDispatch = useDispatch();
  const { sendRequest: updateTimezone } = useHttpRequests();

  const [timezoneDetails, setTimezoneDetails] = useReducer(
    (state, updates) => ({ ...state, ...updates }),
    timezoneAttributes
  );

  const [isValid, setIsValid] = useReducer(
    (state, updates) => ({ ...state, ...updates }),
    initialTimezoneIsValid
  );

  const updateTimezoneDetailsValue = (_key, value) => {
    setTimezoneDetails({ [_key]: value });
    if (value) {
      updateErrorMessage([_key], "", "");
    }
  };

  const updateErrorMessage = (_key, header, errMsg) => {
    setIsValid({ [_key]: errMsg + header });
  };

  const onChangeHandler = (e, controlName) => {
    updateTimezoneDetailsValue(
      controlName,
      e.target.type === "checkbox" ? e.target.checked : e.target.value
    );
  };

  const cancelTimezoneHandler = () => {
    messageDispatch(setMessageHandler({ type: null, message: null }));
    setTimezoneEdit(false);
    setUpdateOpen(false);
  };

  const validateTimezoneDetails = () => {
    setIsValid(initialTimezoneIsValid);

    var errorSummary = "";

    if (!timezoneDetails.countryName) {
      updateErrorMessage("countryName", "Country Name", "Enter ");
      errorSummary += "Enter country name \n";
    } else if (!utils.isValidCharacters(timezoneDetails.countryName)) {
      updateErrorMessage(
        "countryName",
        "country name",
        " has restricted characters"
      );
      errorSummary += "Country name has restricted characters \n";
    }

    if (!timezoneDetails.description) {
      updateErrorMessage("description", "Timezone Description", "Enter ");
      errorSummary += "Enter timezone description \n";
    } else if (!utils.isValidCharacters(timezoneDetails.description)) {
      updateErrorMessage(
        "description",
        "Timezone description",
        " has restricted characters"
      );
      errorSummary += "Timezone description has restricted characters \n";
    }

    if (
      timezoneDetails.diff === "" ||
      isNaN(timezoneDetails.diff) ||
      !Number.isInteger(Number(timezoneDetails.diff)) ||
      parseInt(timezoneDetails.diff, 10) < -12 ||
      parseInt(timezoneDetails.diff, 10) > 14
    ) {
      updateErrorMessage("diff", "Difference", "Enter ");
      errorSummary += "Invalid GMT difference \n";
    }

    if (errorSummary !== "") {
      messageDispatch(
        setMessageHandler({ type: "error", message: errorSummary })
      );
    } else {
      const dataObj = timezoneDetails;

      const getUpdateTimezoneResponse = (resObj) => {
        if (
          !resObj ||
          !resObj["isSuccess"] ||
          (resObj["validationErrors"] !== undefined &&
            resObj["validationErrors"]?.length > 0)
        ) {
          messageDispatch(
            setMessageHandler({
              type: "error",
              message: resObj["message"]
                ? resObj["message"]
                : "" +
                  (resObj["validationErrors"]?.length > 0
                    ? "\n" + resObj["validationErrors"]?.join("\n")
                    : ""),
            })
          );
          return;
        }
        messageDispatch(
          setMessageHandler({
            type: "success",
            message: "Timezone updated successfully!",
          })
        );
        setTimeout(() => {
          cancelTimezoneHandler();
        }, 2000);
      };

      if (timezoneEdit) {
        updateTimezone(
          {
            url: "Timezones/" + dataObj["timezoneKey"],
            method: "PUT",
            body: dataObj,
          },
          getUpdateTimezoneResponse
        );
      } else {
        delete dataObj["timezoneKey"];
        updateTimezone(
          { url: "Timezones", body: dataObj },
          getUpdateTimezoneResponse
        );
      }
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          overflow: "hidden",
          padding: "0px",
        }}
      >
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          <MessageDisplay />
          <InputText
            type="text"
            label="Country Name"
            id={"timezoneDetails.countryName"}
            value={timezoneDetails.countryName}
            onChange={(e) => onChangeHandler(e, "countryName")}
            isRequired={true}
            isError={isValid["countryName"] !== ""}
            charLength={50}
          />

          <InputText
            type="text"
            label="Timezone Description"
            id={"timezoneDetails.description"}
            value={timezoneDetails.description}
            onChange={(e) => onChangeHandler(e, "description")}
            isRequired={true}
            isError={isValid["description"] !== ""}
            charLength={50}
          />

          <InputText
            type="number"
            label="GMT Difference"
            id={"timezoneDetails.diff"}
            value={timezoneDetails.diff}
            onChange={(e) => onChangeHandler(e, "diff")}
            isRequired={true}
            isError={isValid["diff"] !== ""}
            charLength={3}
            max={14}
            min={-12}
          />

          <StatusControl
            label="DST"
            isRequired={false}
            fieldName={"isDst"}
            active={timezoneDetails.isDst}
            updateStatusValue={updateTimezoneDetailsValue}
          />

          <StatusControl
            label="Active"
            isRequired={false}
            fieldName={"active"}
            active={timezoneDetails.active}
            updateStatusValue={updateTimezoneDetailsValue}
          />
          <SaveCancel
            cancelCall={() => cancelTimezoneHandler()}
            saveCall={() => validateTimezoneDetails()}
          />
        </div>
      </Box>
    </>
  );
};
*/
